"use client"

import type React from "react"

import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Check, Search, Loader2, X } from "lucide-react"

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Virtualized list item component
interface VirtualizedItemProps<T = any> {
  item: T
  isSelected: boolean
  isHighlighted: boolean
  onClick: () => void
  onMouseEnter: () => void
  getItemLabel?: (item: T) => string
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode
}

const VirtualizedItem = <T,>({ 
  item, 
  isSelected, 
  isHighlighted, 
  onClick, 
  onMouseEnter, 
  getItemLabel,
  renderItem 
}: VirtualizedItemProps<T>) => (
  <div
    className={cn(
      "relative flex h-full w-full min-w-0 cursor-default select-none items-center overflow-hidden rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
      "hover:bg-accent hover:text-accent-foreground",
      isHighlighted && "bg-accent text-accent-foreground",
    )}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      {isSelected && <Check className="h-4 w-4" />}
    </span>

    {renderItem ? (
      renderItem(item, isSelected)
    ) : (
      <span className="min-w-0 flex-1 truncate text-left">{getItemLabel ? getItemLabel(item) : String(item)}</span>
    )}
  </div>
)

// Searchable dropdown component (just the dropdown content)
interface SearchableDropdownProps<T = any> {
  options: T[]
  value?: string
  onValueChange: (value: string) => void
  // Mapping helpers
  getItemLabel?: (item: T) => string
  getItemValue?: (item: T) => string
  // UI & behaviour
  searchPlaceholder?: string
  emptyMessage?: string
  searchable?: boolean
  // Custom rendering
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode
}

const SearchableDropdown = <T,>({
  options,
  value,
  onValueChange,
  getItemLabel = (item: T) => String(item),
  getItemValue = (item: T) => String(item),
  searchPlaceholder = "Search options...",
  emptyMessage,
  searchable = true,
  renderItem,
}: SearchableDropdownProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [isSearching, setIsSearching] = useState(false)

  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  // Tracks whether the last highlight change came from the keyboard. Mouse hover also moves the
  // highlight; auto-scrolling on hover fights the user's wheel scroll (jitter + the top of the list
  // becomes unreachable), so we only scroll the highlighted item into view for keyboard navigation.
  const isKeyboardNav = useRef(false)
  // Scroll plumbing — see handleScroll. We throttle the windowing setState to one update per animation
  // frame, and suppress hover-driven highlight changes while a scroll is in flight (rows sliding under a
  // stationary cursor fire onMouseEnter, which would otherwise thrash highlight + re-render = jitter).
  const latestScrollTop = useRef(0)
  const scrollRaf = useRef<number | null>(null)
  const isScrolling = useRef(false)
  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (scrollRaf.current != null) cancelAnimationFrame(scrollRaf.current)
    if (scrollIdleTimer.current) clearTimeout(scrollIdleTimer.current)
  }, [])

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!debouncedSearchTerm || !searchable) return options

    return options.filter((option) => 
      getItemLabel(option).toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    )
  }, [options, debouncedSearchTerm, searchable, getItemLabel])

  // Show loading state while debouncing
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true)
    } else {
      setIsSearching(false)
    }
  }, [searchTerm, debouncedSearchTerm])

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0)
  }, [filteredOptions])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          isKeyboardNav.current = true
          setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev))
          break
        case "ArrowUp":
          e.preventDefault()
          isKeyboardNav.current = true
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case "Enter":
          e.preventDefault()
          if (filteredOptions[highlightedIndex]) {
            onValueChange(getItemValue(filteredOptions[highlightedIndex]))
          }
          break
      }
    },
    [filteredOptions, highlightedIndex, onValueChange, getItemValue],
  )

  // Scroll the highlighted item into view — keyboard navigation only. Skipped for hover-driven
  // highlight changes (and the filter-reset to index 0), so wheel scrolling is never hijacked.
  // Instant ("auto") rather than "smooth" to avoid animating while the virtualized window re-renders.
  useEffect(() => {
    if (!isKeyboardNav.current) return
    isKeyboardNav.current = false
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" })
  }, [highlightedIndex])

  // Virtual scrolling parameters
  const ITEM_HEIGHT = 32 // Height of each item in pixels (py-1.5 = 6px top + 6px bottom + ~20px content)
  const CONTAINER_HEIGHT = 300 // Max height of the dropdown
  const VISIBLE_ITEMS = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT)

  // Calculate which items to render (simple virtualization)
  const [scrollTop, setScrollTop] = useState(0)
  
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 1) // Add buffer above
  const endIndex = Math.min(startIndex + VISIBLE_ITEMS + 4, filteredOptions.length) // Add more buffer
  const visibleItems = filteredOptions.slice(startIndex, endIndex)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    latestScrollTop.current = e.currentTarget.scrollTop

    // Mark a scroll as in progress and clear it once the wheel/trackpad goes idle, so onMouseEnter
    // (which fires as rows slide under a still cursor) doesn't fight the scroll by moving the highlight.
    isScrolling.current = true
    if (scrollIdleTimer.current) clearTimeout(scrollIdleTimer.current)
    scrollIdleTimer.current = setTimeout(() => { isScrolling.current = false }, 120)

    // Coalesce the windowing recompute to one setState per frame instead of one per scroll event.
    if (scrollRaf.current != null) return
    scrollRaf.current = requestAnimationFrame(() => {
      scrollRaf.current = null
      setScrollTop(latestScrollTop.current)
    })
  }

  return (
    <div className="flex flex-col">
      {/* Search Input */}
      {searchable && (
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-2 py-0 bg-transparent form-field-focus"
            onKeyDown={handleKeyDown}
          />
          {isSearching && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          {searchTerm && (
            <Button variant="ghost" size="sm" className="ml-2 h-4 w-4 p-0" onClick={() => setSearchTerm("")}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}

      {/* Virtualized List */}
      <div
        className="relative overflow-y-auto overflow-x-hidden overscroll-contain"
        style={{ 
          height: filteredOptions.length === 0 
            ? 'auto'  // Let empty state determine height
            : Math.min(CONTAINER_HEIGHT, filteredOptions.length * ITEM_HEIGHT),
          maxHeight: '40vh', // Ensure it doesn't exceed viewport height in dialogs
          minHeight: filteredOptions.length === 0 ? 'auto' : '100px' // Only enforce min height when we have options
        }}
        onScroll={handleScroll}
        onWheel={(e) => {
          // Ensure wheel events work in dialogs
          e.stopPropagation()
        }}
      >
        {/* Virtual spacer for items above viewport */}
        <div style={{ height: startIndex * ITEM_HEIGHT }} />

        {/* Visible items */}
        {visibleItems.map((item, index) => {
          const actualIndex = startIndex + index
          const itemValue = getItemValue(item)
          return (
            <div key={itemValue} ref={(el) => { itemRefs.current[actualIndex] = el }} style={{ height: ITEM_HEIGHT }}>
              <VirtualizedItem
                item={item}
                isSelected={itemValue === value}
                isHighlighted={actualIndex === highlightedIndex}
                onClick={() => {
                  onValueChange(itemValue)
                }}
                onMouseEnter={() => { if (!isScrolling.current) setHighlightedIndex(actualIndex) }}
                getItemLabel={getItemLabel}
                renderItem={renderItem}
              />
            </div>
          )
        })}

        {/* Virtual spacer for items below viewport */}
        <div style={{ height: (filteredOptions.length - endIndex) * ITEM_HEIGHT }} />

        {/* Empty state */}
        {filteredOptions.length === 0 && !isSearching && (
          <div className="flex items-center justify-center py-4 px-3 text-sm text-muted-foreground">
            {emptyMessage || "No options found"}
          </div>
        )}
      </div>
    </div>
  )
}

export { SearchableDropdown }
